/**
 * /admin/provision — manually provision a new tenant.
 * On success, redirects to /admin/accounts/:id.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../admin_api';

const CURRENCIES = ['GBP', 'EUR', 'USD', 'CAD', 'AUD', 'NZD'];
const TIMEZONES = [
  'Europe/London',
  'Europe/Dublin',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
];

function Field({
  label,
  id,
  ...props
}: {
  label: string;
  id: string;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium block mb-1">
        {label}
      </label>
      <input
        id={id}
        {...props}
        className="w-full text-sm border border-gray-200 rounded px-3 py-1.5"
      />
    </div>
  );
}

function SelectField({
  label,
  id,
  options,
  value,
  onChange,
}: {
  label: string;
  id: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium block mb-1">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm border border-gray-200 rounded px-3 py-1.5"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

export function AdminProvision() {
  const navigate = useNavigate();

  const [accountSlug, setAccountSlug] = useState('');
  const [accountName, setAccountName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [timezone, setTimezone] = useState('Europe/London');
  const [locale, setLocale] = useState('en-GB');
  const [facilitySlug, setFacilitySlug] = useState('main');
  const [facilityName, setFacilityName] = useState('Main Facility');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await adminApi.provisionTenant({
        accountSlug,
        accountName,
        ownerEmail,
        ownerName,
        currency,
        defaultTimezone: timezone,
        locale,
        firstFacilitySlug: facilitySlug,
        firstFacilityName: facilityName,
      });
      navigate(`/admin/accounts/${result.account.id}`);
    } catch (e) {
      setError((e as Error).message);
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-xl">
      <h1 className="text-xl font-semibold mb-6">Provision new tenant</h1>

      <form
        onSubmit={handleSubmit}
        className="space-y-4 bg-white rounded-lg border border-[oklch(88%_0.05_70)] p-6"
      >
        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Account slug"
            id="accountSlug"
            value={accountSlug}
            onChange={(e) => setAccountSlug(e.target.value)}
            placeholder="levern-bank"
            required
          />
          <Field
            label="Account name"
            id="accountName"
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            placeholder="Levern Bank Kennels"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Owner email"
            id="ownerEmail"
            type="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            placeholder="owner@example.com"
            required
          />
          <Field
            label="Owner name"
            id="ownerName"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder="Jordan Craig"
            required
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <SelectField
            label="Currency"
            id="currency"
            options={CURRENCIES}
            value={currency}
            onChange={setCurrency}
          />
          <SelectField
            label="Timezone"
            id="timezone"
            options={TIMEZONES}
            value={timezone}
            onChange={setTimezone}
          />
          <div>
            <label htmlFor="locale" className="text-sm font-medium block mb-1">
              Locale
            </label>
            <input
              id="locale"
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              placeholder="en-GB"
              className="w-full text-sm border border-gray-200 rounded px-3 py-1.5"
            />
          </div>
        </div>

        <hr className="border-gray-100" />

        <div className="grid grid-cols-2 gap-4">
          <Field
            label="First facility slug"
            id="facilitySlug"
            value={facilitySlug}
            onChange={(e) => setFacilitySlug(e.target.value)}
            placeholder="main"
            required
          />
          <Field
            label="First facility name"
            id="facilityName"
            value={facilityName}
            onChange={(e) => setFacilityName(e.target.value)}
            placeholder="Main Facility"
            required
          />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-2 bg-[#004AAD] text-white text-sm font-medium rounded disabled:opacity-40"
        >
          {submitting ? 'Provisioning…' : 'Provision tenant'}
        </button>
      </form>
    </div>
  );
}
